import React, { useEffect, useState, useCallback } from 'react';
import SecretariatShell from './SecretariatShell';
import { SectionTitle } from './PageShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { X, Loader2, Pencil, Trash2 } from 'lucide-react';

const COLORS = ['#dc2626', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
const EMPTY = { name: '', email: '', phone: '', authorization_number: '', specialties: [], color: COLORS[0], notes: '' };

export default function SecretariatEnseignantsPage() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [formules, setFormules] = useState({});
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const refresh = useCallback(() => { api.secTeachers().then((d) => setItems(d.items || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    refresh();
    api.secFormules().then((d) => setFormules(d.formules || {})).catch(() => {});
  }, [authLoading, user, refresh]);

  if (authLoading) return <SecretariatShell><div className="p-12 text-white/50">Chargement…</div></SecretariatShell>;
  if (!user || (user.role !== 'secretaire' && !user.is_admin && !user.secretariat_access)) return <SecretariatShell><div className="p-12 text-center text-white/60">Accès réservé au secrétariat.</div></SecretariatShell>;

  const save = async () => {
    setSaving(true); setErrMsg('');
    const payload = { name: form.name, email: form.email, phone: form.phone, authorization_number: form.authorization_number || '', specialties: form.specialties, color: form.color, notes: form.notes || '' };
    try {
      if (modal === 'create') await api.secCreateTeacher(payload);
      else await api.secUpdateTeacher(form.id, payload);
      setModal(null); refresh();
    } catch (e) { setErrMsg(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const remove = async (f) => {
    if (!window.confirm(`Supprimer la fiche de ${f.name} (et ses leçons) ?`)) return;
    await api.secDeleteTeacher(f.id).catch(() => {});
    refresh();
  };

  const toggleSpec = (v) => setForm((f) => ({ ...f, specialties: f.specialties.includes(v) ? f.specialties.filter((x) => x !== v) : [...f.specialties, v] }));

  return (
    <SecretariatShell>
      <section className="max-w-6xl mx-auto px-4 lg:px-6 mt-8" data-testid="secretariat-enseignants">
        <SectionTitle kicker="Secrétariat · Enseignants" title="Fiches enseignants" sub="Créez les fiches et définissez les spécialités (formules enseignées) et la couleur planning." />
        <div className="flex justify-end mb-5">
          <button onClick={() => { setForm(EMPTY); setErrMsg(''); setModal('create'); }} data-testid="sec-teacher-create-btn" className="h-10 px-5 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-[13px] font-bold">+ Nouvelle fiche enseignant</button>
        </div>

        {items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-white/45 text-[13px]" data-testid="sec-teachers-empty">Aucune fiche enseignant — créez la première !</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((f) => (
              <div key={f.id} className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5" data-testid={`sec-teacher-card-${f.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-[15px]" style={{ background: f.color }}>{f.name.charAt(0).toUpperCase()}</span>
                  <div className="min-w-0">
                    <div className="text-white font-semibold truncate">{f.name}</div>
                    <div className="text-white/40 text-[11.5px] truncate">{f.email || f.phone || '—'}</div>
                  </div>
                  <div className="ml-auto flex">
                    <button onClick={() => { setForm({ ...f }); setErrMsg(''); setModal(f); }} data-testid={`sec-teacher-edit-${f.id}`} className="text-white/50 hover:text-white p-1.5"><Pencil size={14} /></button>
                    <button onClick={() => remove(f)} className="text-red-400/60 hover:text-red-300 p-1.5"><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(f.specialties || []).length === 0 ? <span className="text-white/35 text-[11.5px]">Aucune spécialité définie</span> :
                    f.specialties.map((s) => <span key={s} className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60 text-[10.5px] font-semibold">{formules[s]?.label || s}</span>)}
                </div>
                {f.authorization_number && <div className="mt-2 text-amber-400/70 text-[10.5px] font-mono-spec">Autorisation : {f.authorization_number}</div>}
                {f.account_linked === false && (
                  <div className="mt-2 rounded-lg bg-orange-950/30 border border-orange-700/40 px-2.5 py-1.5 text-orange-300 text-[10.5px] font-semibold" data-testid={`teacher-unlinked-${f.id}`}>
                    ⚠ Aucun compte utilisateur avec l'email « {f.email || '—'} » : son planning « Ma journée » restera vide. L'email de la fiche doit être identique à son email de connexion.
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {modal && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setModal(null)}>
            <div className="w-full max-w-md rounded-2xl border border-white/15 bg-[#111113] p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sec-teacher-modal">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-serif-editorial text-white text-[20px]">{modal === 'create' ? 'Nouvelle fiche enseignant' : `Modifier ${form.name}`}</h3>
                <button onClick={() => setModal(null)} className="text-white/50 hover:text-white"><X size={18} /></button>
              </div>
              <div className="space-y-3">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom et prénom *" data-testid="sec-teacher-name-input" className="w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]" />
                <div className="grid grid-cols-2 gap-2">
                  <input value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (lie son compte)" className="w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]" />
                  <input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone" className="w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]" />
                </div>
                <label className="block text-[12px] text-white/55">N° d'autorisation d'enseigner (obligatoire DSR)
                  <input value={form.authorization_number || ''} onChange={(e) => setForm({ ...form, authorization_number: e.target.value })} placeholder="Ex : A 02 012 3456 0" data-testid="sec-teacher-auth-input" className="mt-1 w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13px]" />
                </label>
                <div>
                  <div className="text-[12px] text-white/55 mb-1.5">Spécialités (formules enseignées)</div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(formules).map(([v, f]) => (
                      <button key={v} onClick={() => toggleSpec(v)} data-testid={`sec-teacher-spec-${v}`} className={`px-2.5 h-7 rounded-full border text-[11px] font-semibold ${form.specialties?.includes(v) ? 'bg-amber-500 text-black border-amber-500' : 'text-white/55 border-white/15'}`}>{f.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[12px] text-white/55 mb-1.5">Couleur planning</div>
                  <div className="flex gap-2">
                    {COLORS.map((c) => (
                      <button key={c} onClick={() => setForm({ ...form, color: c })} className={`w-7 h-7 rounded-full border-2 ${form.color === c ? 'border-white' : 'border-transparent'}`} style={{ background: c }} />
                    ))}
                  </div>
                </div>
                <textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes internes" rows={2} className="w-full rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-white text-[13px]" />
                {errMsg && <div className="text-red-300 text-[12.5px]">⚠️ {String(errMsg)}</div>}
                <button onClick={save} disabled={saving || form.name.trim().length < 2} data-testid="sec-teacher-save-btn" className="w-full h-10 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black text-[13px] font-bold inline-flex items-center justify-center gap-2">
                  {saving && <Loader2 size={14} className="animate-spin" />} Enregistrer
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </SecretariatShell>
  );
}
